import { Dispatch } from 'redux';
import { v1 as uuidv1 } from 'uuid';

import { getMainPrivateApi } from 'api/apiCoreSdk';
import { isUserLoggedOutError, KatanaApiError } from 'api/apiPatchConstants';
import { getWalletProofSignatureTypedData } from 'api/signatures';
import { APIUser } from 'api/types';
import { trackEvent } from 'lib/analytics';
import { signTypedData } from 'lib/ethereum/sign/typedData/eip712';
import { disconnectWallet } from 'lib/ethereum/wallets';
import { userSigningOut } from 'reduxState/currentUserData/userProcesses';
import { DelegatedKeyAuthorization } from 'reduxState/delegatedKey/authorizationReducer';
import { getStateOutsideOfComponent, staticDispatch } from 'reduxState/store';
import { ReduxActions } from 'reduxState/storeCore';
import { IWallet, removeWallet } from 'reduxState/wallet/walletReducer';
import { WalletType } from 'reduxState/wallet/walletUtils';

import { changeUser } from './currentUserReducer';
import { UserType } from './currentUserType';

async function revokeDelegatedKey(
  wallet: IWallet | null,
  delegatedWallet: DelegatedKeyAuthorization | undefined
) {
  // Revoke delegated key if it wont cause any orders to be canceled
  if (wallet && delegatedWallet?.walletAddress) {
    const nonce = uuidv1();
    try {
      await getMainPrivateApi().revokeDelegatedKey({
        wallet: delegatedWallet.custodyWalletAddress,
        nonce,
        delegatedKey: delegatedWallet.walletAddress,
        neverRevokeWithOpenOrders: true,
        // a dk can revoke itself so no modal is needed
        signature: await signTypedData(
          wallet,
          delegatedWallet,
          await getWalletProofSignatureTypedData({ nonce })
        ),
      });
    } catch (error) {
      // we dont really care if it fails due to open orders here, we just want to remove a delegated key
      // if it has no open orders so the user doesn't have excess useless delegated keys
      //
      // at some point we may have access to if there are open orders for a given dk at which point we
      // can call this conditionally.
      console.warn(error);
    }
  }
}

/**
 * Second part of full logout flow - without Revoking delegated key
 *  - used after Delegated Key session expired
 */
export async function logoutAndRemoveWallet({
  walletType,
  currentUser,
}: {
  walletType: WalletType | undefined | null;
  currentUser: UserType | undefined;
}) {
  await getMainPrivateApi()
    .authLogout()
    .catch(error => {
      console.warn(error);
      console.error('Logout problem');
      if (currentUser && currentUser.id && !currentUser.isCatchAllUser) {
        // We can't ignore this for API users
        throw new Error(error);
      }
    });

  if (walletType) {
    disconnectWallet(walletType);
  }

  staticDispatch(removeWallet());
  staticDispatch(changeUser(false));

  setTimeout(() => fetchAndDispatchUser(staticDispatch));
}

/** Full logout flow - invalidate DK, logout user, remove wallet */
export async function fullLogoutAndDispatch(
  wallet: IWallet | null,
  delegatedWallet: DelegatedKeyAuthorization | undefined,
  dispatch: Dispatch<ReduxActions> = staticDispatch
): Promise<void> {
  trackEvent('user_logout');
  dispatch(userSigningOut());

  await revokeDelegatedKey(wallet, delegatedWallet);

  const { currentUser } = await getStateOutsideOfComponent();

  await logoutAndRemoveWallet({
    currentUser,
    walletType: wallet?.walletType,
  });
}

export const fetchAndDispatchUser = (dispatch: Dispatch<ReduxActions>) =>
  getMainPrivateApi()
    .getUserInfo()
    .catch((error: KatanaApiError) => {
      if (isUserLoggedOutError(error)) {
        dispatch(changeUser(false));
        return;
      }
      // In case it's unexpected error, we should retry..
      // Eg. user offline, server down, etc..
      console.warn(error);
      console.warn('Problem getting user, retry in 3 seconds');
      return new Promise<APIUser | void>(resolve => {
        setTimeout(() => {
          resolve(
            getMainPrivateApi()
              .getUserInfo()
              // eslint-disable-next-line promise/no-nesting
              .catch(error => {
                console.warn(error);
                if (isUserLoggedOutError(error)) {
                  dispatch(changeUser(false));
                  return;
                }
                // After failed retry UI will be in not fully loaded state tho
                // WebsocketSubscriptionManager will try retry in ~15 seconds
              })
          );
        }, 3000);
      });
    })
    // .then(response => {
    //   if (!response) {
    //     return;
    //   }
    //   const kycApplications = response?.kyc?.applications || {};
    //   const kycHighestAppliedTier = Object.keys(kycApplications).length;
    //   return {
    //     ...response,
    //     kycHighestAppliedTier,
    //     kycNoApplications: Object.keys(kycApplications).length === 0,
    //   };
    // })
    .then(async response => {
      if (!response) {
        return;
      }
      // const kycTier = response?.kyc?.tier || 0;
      // const { isBanned } = response;
      // const pendingStatusRaw = response?.kyc?.applications
      //   ? response.kyc.applications[(kycTier || 0) + 1]?.status
      //   : undefined;
      // const tier2StatusRaw = (response?.kyc?.applications || {})['2']?.status;
      // const tier2DataSubmitedState = tier2StatusRaw === null;

      // const tier2Metadata = (response?.kyc?.applications || {})['2']?.metadata;
      // const isProofOfAddress =
      //   kycTier === 1 &&
      //   ![
      //     // Ignore POA in those states
      //     tierStatusMap.ACCEPT,
      //     tierStatusMap.REJECT,
      //     tierStatusMap.BANNED,
      //   ].includes(tier2StatusRaw) &&
      //   (tier2Metadata?.requirements || []).includes('poa');

      // const pendingKycStatus = isBanned
      //   ? tierStatusMap.BANNED
      //   : pendingStatusRaw
      //   ? tierStatusMap[pendingStatusRaw as keyof typeof tierStatusMap]
      //     ? isProofOfAddress
      //       ? tierStatusMap.RESUBMIT_WITH_POA // Generated RESUBMIT Sub-state to allow POA
      //       : pendingStatusRaw
      //     : tierStatusMap.UNKNOWN
      //   : tier2DataSubmitedState // When state is `null`, check if it's T2
      //   ? tierStatusMap.PENDING // Backend or generated state, when T2 state === null
      //   : tierStatusMap.NONE;

      // if (pendingKycStatus === tierStatusMap.UNKNOWN) {
      //   console.error(`User is in UNKNOWN tier state - ${pendingStatusRaw}`);
      // }
      // if (kycTier === -1) {
      //   console.warn('User error code #612387');
      //   //   throw new Error(
      //   //     t('auth:::Username/password combination is incorrect.')
      //   //   );
      // }

      const userPayload = {
        id: response.id,
        isCatchAllUser: response.isCatchAllUser,
        email: response.email,
        magicCode: response?.entry?.code || '',

        firstName: response.profile?.firstName || '',
        lastName: response.profile?.lastName || '',
        companyName: response.profile?.companyName || '',

        intercomHash: response?.intercom?.hash || '',
        intercomId: response?.intercom?.id || '',

        // kycTier,
        // kycNoApplications: response.kycNoApplications,
        // pendingKycStatus,
      };
      const userExtendedData = {
        // firstName: response?.profile?.firstName,
        // lastName: response?.profile?.lastName,
        telephone: response?.profile?.telephone,
        countryCode: response?.profile?.country,
        // intercomHash: response?.intercom?.verification,
        address: response?.profile?.address,
      };

      // User is logged in
      dispatch(changeUser(true, userPayload, userExtendedData));

      // dispatch(
      //   setUserCompetitionInfo({
      //     referralCode: response?.entry?.userName || '',
      //     magicLinkCode: response?.entry?.code || '',
      //   })
      // );

      // Fetch wallets asynchronously
      // fetchAndDispatchUserWallets(dispatch);
      // fetchAndDispatchUserSettings(userPayload.id);

      return response; // { ...response, pendingKycStatus };
    });
